import { dateFormat } from '@carboncopyco/utils';
import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import HC_rounded from 'highcharts-rounded-corners';
import type { ColumnChartConfigType } from '../../types';
import { getDateFromToInFormat } from '../../utils';

HC_rounded(Highcharts);

export const getBaseConfig = (
  data: ColumnChartConfigType
): Highcharts.Options => ({
  title: { text: '' },
  exporting: { enabled: false },
  credits: {
    enabled: false,
  },
  navigation: {
    buttonOptions: {
      enabled: true,
    },
  },
  series: data?.series,
  yAxis: {
    title: {
      text: '',
    },
    allowDecimals: false,
    tickAmount: data?.tickAmount,
    endOnTick: false,
    zoomEnabled: false,
    showFirstLabel: true,
    showLastLabel: true,
    labels: {
      style: {
        color: '#9B9AA2',
        fontSize: '10px',
        fontFamily: 'Inter',
        fontWeight: '400px',
      },
      enabled: true,
    },
    gridLineColor: '#F1F1F3',
  },
  xAxis: {
    categories: data?.xCategories,
    zoomEnabled: data?.zoomEnabled,
    labels: {
      step: data?.step,
      enabled: true,
      rotation: 0,
      style: {
        color: '#9B9AA2',
        fontSize: '10px',
        fontFamily: 'Inter',
        fontWeight: '400px',
      },
      formatter: function () {
        const { value, axis, pos } = this;
        const val = String(value);
        const numericDayLabel = dateFormat(val, 'D');
        const prevValue = axis.categories[pos - (data.step || 1)];
        const shouldShowMonthLabel =
          dayjs(prevValue).get('M') !== dayjs(value).get('M');
        return shouldShowMonthLabel
          ? `${numericDayLabel}</br>${dateFormat(val, 'MMM')}`
          : numericDayLabel;
      },
    },
    lineColor: '#F1F1F3',
  },
  chart: {
    backgroundColor: 'none',
    height: data?.chartHeight ?? 220,
    type: 'column',
    animation: true,
  },
  tooltip: {
    backgroundColor: '#37363F',
    style: {
      opacity: 0.8,
      color: '#F9F9F9',
      shadow: '2px 4px 14 30',
      fontFamily: 'Inter',
    },
    borderRadius: 6,
    borderColor: '#37363F',
    useHTML: true,
    padding: 12,
    formatter: function () {
      const formattedDate =
        data?.resolution === 'week'
          ? getDateFromToInFormat(String(this.x))
          : data?.resolution === 'month'
            ? dateFormat(String(this.x), 'MMM, YYYY')
            : dateFormat(String(this.x), 'MMM D, YYYY');

      const square = `<span class="inline-block w-3 h-3 rounded-sm pt-1.5 mr-1.5" style=background-color:${this.point.color};"></span>`;
      return `${formattedDate} <br /> <div class="flex items-center mt-2">${square} ${this.series.name}: <b class="ml-1">${this.y}</b> </div>`;
    },
  },
  plotOptions: {
    column: {
      centerInCategory: true,
      maxPointWidth: 80,
      stacking: 'normal',
      borderWidth: 2,
    },
  },
  legend: {
    enabled: true,
    margin: 0,
    symbolHeight: 12,
    symbolWidth: 12,
    symbolRadius: 2,
    itemMarginTop: 12,
    itemMarginBottom: 0,
    padding: 0,
    reversed: true,
    itemStyle: {
      color: '#5F5E65',
      fontFamily: 'inter',
      fontWeight: 'normal',
      fontSize: '12px',
    },
    itemHoverStyle: {
      cursor: 'default',
    },
    itemDistance: 20,
    align: 'left',
    verticalAlign: 'bottom',
  },
});
